import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { YoutubeConnectCard } from "../components/YoutubeConnectCard";
import { CompilerPlaylistGrid } from "../components/CompilerPlaylistGrid";
import { CompilerVideoSelector } from "../components/CompilerVideoSelector";
import { ProjectCard } from "../components/ProjectCard";
import { api } from "../lib/api";
import { toast } from "sonner";

const activeStatuses = ["pending", "script", "voiceover", "visuals", "assembly"];

export default function CompilerPage() {
  const navigate = useNavigate();
  const [connected, setConnected] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [playlist, setPlaylist] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [titles, setTitles] = useState({});
  const [titleHint, setTitleHint] = useState("");
  const [privacy, setPrivacy] = useState("private");
  const [compiling, setCompiling] = useState(false);
  const [projects, setProjects] = useState([]);

  const loadProjects = async () => {
    try {
      const res = await api.get("/projects");
      setProjects(res.data.filter((p) => p.kind === "compilation"));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadProjects();
    const interval = setInterval(() => {
      setProjects((prev) => {
        if (prev.some((p) => activeStatuses.includes(p.status))) loadProjects();
        return prev;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadPlaylists = async () => {
    try {
      const res = await api.get("/compiler/playlists");
      setPlaylists(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load playlists");
    }
  };

  const handleStatusChange = (status) => {
    setConnected(status.connected);
    if (status.connected) loadPlaylists();
  };

  const openPlaylist = async (pl) => {
    try {
      const res = await api.get(`/compiler/playlists/${pl.id}/videos`);
      setVideos(res.data);
      setPlaylist(pl);
      setSelected([]);
      setTitles({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load playlist videos");
    }
  };

  const toggleVideo = (id, title) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
    setTitles((prev) => ({ ...prev, [id]: title }));
  };

  const compile = async () => {
    setCompiling(true);
    try {
      const res = await api.post("/compiler/compile", {
        playlist_id: playlist.id,
        playlist_title: playlist.title,
        video_ids: selected,
        video_titles: selected.map((id) => titles[id]),
        title_hint: titleHint,
        publish_privacy: privacy,
      });
      toast.success("Compilation started!");
      navigate(`/project/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start compilation");
    } finally {
      setCompiling(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p.id !== id));
      toast.success("Compilation deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen">
      <Header onNewProject={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-50 mb-1">Bhajan / Song Compiler</h1>
          <p className="text-sm text-slate-500">
            Pick a playlist from your own channel, choose which videos to stitch together, and upload the
            compilation back with an AI-written SEO title/description/tags and a custom thumbnail.
          </p>
        </div>

        <YoutubeConnectCard slot="compiler" label="Compiler" onStatusChange={handleStatusChange} />

        {connected && !playlist && (
          <CompilerPlaylistGrid playlists={playlists} onSelect={openPlaylist} />
        )}

        {connected && playlist && (
          <CompilerVideoSelector
            playlist={playlist}
            videos={videos}
            selectedIds={selected}
            onToggle={toggleVideo}
            onSelectAll={() => {
              setSelected(videos.map((v) => v.video_id));
              setTitles(Object.fromEntries(videos.map((v) => [v.video_id, v.title])));
            }}
            onClear={() => setSelected([])}
            onBack={() => setPlaylist(null)}
            titleHint={titleHint}
            onTitleHint={setTitleHint}
            privacy={privacy}
            onPrivacy={setPrivacy}
            onCompile={compile}
            compiling={compiling}
          />
        )}

        <section>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-50 mb-6">Your Compilations</h2>
          {projects.length === 0 ? (
            <p data-testid="compiler-empty-state" className="text-slate-500 text-sm">No compilations yet. Connect your channel and compile your first one above!</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {projects.map((p, idx) => (
                <div key={p.id} style={{ animationDelay: `${idx * 0.05}s` }} className="animate-in fade-in">
                  <ProjectCard project={p} onOpen={(id) => navigate(`/project/${id}`)} onDelete={handleDelete} />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
